import { gql } from '@apollo/client';

/**
 * MDM Devices operations — global cross-platform devices view.
 *
 * Distinct from the legacy `Devices` query (which targets the `devices`
 * collection — signage licenses + paired hardware). `mdmDevices` reads the
 * `mdm_devices` collection, aggregating rows across every platform adapter
 * (Zoom Rooms, Teams Rooms, Webex Rooms, Google Meet) and joining linked
 * `devices` Screen rows for the global Devices page (`/app/mdm`).
 *
 * Spec: docs/specs/19-optiedge-mdm/google-meet/02-spec-phase-2.md §5.1, §5.3
 *
 * Phase 0 contract (Google Meet Phase 2):
 *   - `MdmDevicesFilterInput` gains optional `orgUnitPaths: [String!]`
 *     (multi-select OU filter; exact-match against `platformMeta.orgUnitPath`;
 *     empty array = no filter; backwards compatible). [GM2-007]
 *   - Response `platformMeta` is a JSON scalar carrying the 13 Google fields
 *     (chromeOsDeviceId · serialNumber · orgUnitPath · annotatedUser ·
 *     annotatedLocation · annotatedAssetId · resourceId · resourceEmail ·
 *     resourceCategory · buildingId · buildingName · floorName · capacity).
 *     Other vendors continue to attach their own fields through the same JSON
 *     scalar. [GM2-004]
 *   - `distinctOrgUnitPaths` query lives next to `mdmDevices` per spec §5.3
 *     but is exported from `operations/device-platforms.ts` (`DISTINCT_ORG_UNIT_PATHS`)
 *     to keep all platform-related ops co-located.
 */

// MDM_DEVICES_QUERY — global cross-platform list. Refetch after activate /
// disconnect / sync so per-platform filter and OU multi-select stay accurate.
//
// Operation name `MdmDevices` is load-bearing (Apollo `refetchQueries`).
export const MDM_DEVICES_QUERY = gql`
  query MdmDevices(
    $first: Int
    $after: String
    $filter: MdmDevicesFilterInput
    $sort: MdmDeviceSort
  ) {
    mdmDevices(first: $first, after: $after, filter: $filter, sort: $sort) {
      page {
        edges {
          cursor
          node {
            _id
            name
            platform
            managementCapability
            manufacturer
            model
            os
            osVersion
            firmwareVersion
            lastHeartBeat
            availableCommands
            externalRef
            rawAttributes
            signageActivatedAt
            devicesId
            signageUrl
            currentAssetId
            currentPlaylistId
            currentScheduleId
            currentType
            currentName
            createdAt
            updatedAt
            platformMeta
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
      pageData {
        total
      }
    }
  }
`;
